import React, { createContext, useContext, useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { authService } from "../services/auth";
import Papa from "papaparse";

const InstitutionContext = createContext();

// Helper function to parse Python-style list strings with mixed quotes
// Handles formats like: ['item1', "item2"], ["item1", "item2"], or ['item', "it's mixed"]
const parsePythonList = (str) => {
  if (!str || str.trim() === "") return [];

  try {
    // Match either 'single quoted' OR "double quoted" strings
    const matches = str.matchAll(/'([^']*)'|"([^"]*)"/g);
    // Take whichever capture group matched (1 or 2)
    return Array.from(matches, (m) => m[1] || m[2]);
  } catch (e) {
    console.error("Failed to parse list:", str, e);
    return [];
  }
};

export const useInstitution = () => {
  const context = useContext(InstitutionContext);
  if (!context) {
    throw new Error("useInstitution must be used within InstitutionProvider");
  }
  return context;
};

export const InstitutionProvider = ({ children }) => {
  const { institutionId } = useParams();
  const [snippets, setSnippets] = useState([]);
  const [recordings, setRecordings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showPasswordPrompt, setShowPasswordPrompt] = useState(false);

  const handlePasswordSubmit = async (password) => {
    if (!institutionId) return;

    try {
      setIsLoading(true);
      setError(null);

      await authService.verifyInstitution(institutionId, password);
      setIsAuthenticated(true);
      setShowPasswordPrompt(false);

      const { data } = await authService.fetchInstitutionData(institutionId);

      Papa.parse(data, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          const parsedData = results.data.map((row) => ({
            ...row,
            key_moment_tags: parsePythonList(row.key_moment_tags),
          }));

          setSnippets(parsedData);

          const uniqueRecordings = [
            ...new Set(parsedData.map((s) => s.recordingId)),
          ].map((id) => {
            const snippet = parsedData.find((s) => s.recordingId === id);
            return {
              id,
              streamingUrl: snippet.streamingUrl,
              spaceName: snippet.space_name,
              startingTime: snippet.starting_time,
              count: parsedData.filter((s) => s.recordingId === id).length,
            };
          });

          setRecordings(uniqueRecordings);
          setIsLoading(false);
        },
        error: (error) => {
          setError(`Failed to parse CSV: ${error.message}`);
          setIsLoading(false);
        },
      });
    } catch (err) {
      console.error("Failed to load institution data:", err);
      setError(err.message);
      setIsAuthenticated(false);
      setShowPasswordPrompt(true);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const checkAuth = async () => {
      if (!institutionId) {
        setIsLoading(false);
        return;
      }

      if (authService.isInstitutionAuthenticated(institutionId)) {
        try {
          setIsLoading(true);
          const { data } = await authService.fetchInstitutionData(
            institutionId
          );

          Papa.parse(data, {
            header: true,
            skipEmptyLines: true,
            complete: (results) => {
              const parsedData = results.data.map((row) => ({
                ...row,
                key_moment_tags: parsePythonList(row.key_moment_tags),
              }));

              setSnippets(parsedData);

              const uniqueRecordings = [
                ...new Set(parsedData.map((s) => s.recordingId)),
              ].map((id) => {
                const snippet = parsedData.find((s) => s.recordingId === id);
                return {
                  id,
                  streamingUrl: snippet.streamingUrl,
                  spaceName: snippet.space_name,
                  startingTime: snippet.starting_time,
                  count: parsedData.filter((s) => s.recordingId === id).length,
                };
              });

              setRecordings(uniqueRecordings);
              setIsAuthenticated(true);
              setIsLoading(false);
            },
            error: (error) => {
              console.error("Failed to parse CSV:", error);
              authService.logoutInstitution(institutionId);
              setError(`Failed to parse CSV: ${error.message}`);
              setShowPasswordPrompt(true);
              setIsLoading(false);
            },
          });
        } catch (err) {
          console.error("Failed to load institution data:", err);
          authService.logoutInstitution(institutionId);
          setError(err.message || "Failed to load institution data");
          setShowPasswordPrompt(true);
          setIsLoading(false);
        }
      } else {
        setShowPasswordPrompt(true);
        setIsLoading(false);
      }
    };

    checkAuth();
  }, [institutionId]);

  return (
    <InstitutionContext.Provider
      value={{
        institutionId,
        snippets,
        recordings,
        isLoading,
        error,
        isAuthenticated,
        showPasswordPrompt,
        handlePasswordSubmit,
      }}
    >
      {children}
    </InstitutionContext.Provider>
  );
};
